import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
} from '@angular/forms';
import {
  Firestore,
  collection,
  addDoc,
  collectionData,
  doc,
  updateDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  endBefore,
} from '@angular/fire/firestore';
import { Router } from '@angular/router';

import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
} from '@angular/fire/storage';
import { saveAs } from 'file-saver';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
declare var bootstrap: any;

interface RequestOff {
  id?: string;
  employee: string;
  date: string;
  status: string;
  createdAt: string;
  approvedBy: string | null;
  type: string;
  orgId: string;
}

@Component({
  selector: 'app-manager-builder',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './manager-builder.component.html',
  styleUrl: './manager-builder.component.css',
})
export class ManagerBuilderComponent {
  private firestore = inject(Firestore);
  private storage = inject(Storage);

  // 🧱 Form groups
  addEmployeeForm: FormGroup;
  uploadScheduleForm: FormGroup;
  editEmployeeForm: FormGroup;

  // 📆 Utility data
  dayKeys = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  // ⚙️ State
  isSubmitting = false;
  isUploadingSchedule = false;
  showEmployees = false;
  isDeletingInactive = false;
  isDeletingDeclined = false;

  employees: any[] = [];

  selectedFile: File | null = null;
  selectedEmployee: any = null;

  // 🔔 Toast
  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';
  userName: any;

  // Request off
  requestsOff: any[] = [];
  filterStatus: string = 'all';
  pageSize: number = 10;
  lastVisible: any = null;
  firstVisible: any = null;
  isNextDisabled = false;
  isPrevDisabled = true;
  pendingCount: number = 0;
  requestsCount: number = 0;
  currentPage: number = 1;
  totalPages: number = 0;
  sortBy: 'createdAt' | 'date' = 'createdAt';

  // ⚡ PERFORMANCE: Cached counts (calculated once per load)
  inactiveRequestsCount: number = 0;
  deniedRequestsCount: number = 0;

  constructor(
    private fb: FormBuilder,
    public auth: AuthService,
    private router: Router,
  ) {
    // ➕ Add Employee
    this.addEmployeeForm = this.fb.group({
      Name: ['', Validators.required],
      Pin: ['', [Validators.required, Validators.minLength(4)]],
      keyHolder: [false],
      fullTime: [false],
    });

    // 📤 Upload Schedule
    this.uploadScheduleForm = this.fb.group({
      weekStartDate: ['', Validators.required],
      title: [''],
    });

    // 🧑‍💼 Edit Employee (Offcanvas)
    this.editEmployeeForm = this.fb.group({
      Name: [''],
      Pin: [''],
      fullTime: [false],
      keyHolder: [false],
      notes: [''],
      Sunday: [''],
      Monday: [''],
      Tuesday: [''],
      Wednesday: [''],
      Thursday: [''],
      Friday: [''],
      Saturday: [''],
    });
  }

  async ngOnInit() {
    this.userName = this.auth.getUserName();
    this.countPendingRequestsOff();
    this.countAllRequestsOff();
  }

  // ✅ UPLOAD SCHEDULE
  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      const validTypes = [
        'application/pdf',
        'image/png',
        'image/jpeg',
        'image/jpg',
      ];
      const validExtensions = ['pdf', 'png', 'jpg', 'jpeg'];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      if (
        validTypes.includes(file.type) ||
        validExtensions.includes(fileExtension || '')
      ) {
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
          this.showToast('File size must be less than 10MB', 'error');
          event.target.value = '';
          return;
        }

        this.selectedFile = file;
      } else {
        this.showToast('Please upload a valid PDF or PNG file', 'error');
        event.target.value = '';
        this.selectedFile = null;
      }
    }
  }

  async onUploadScheduleSubmit() {
    if (this.uploadScheduleForm.invalid || !this.selectedFile) return;
    this.isUploadingSchedule = true;

    try {
      const { weekStartDate, title } = this.uploadScheduleForm.value;

      const orgId = this.auth.getUsersOrgId();
      if (!orgId) {
        this.showToast('Organization not found for current user.', 'error');
        this.isUploadingSchedule = false;
        return;
      }

      let orgName = 'Unknown Organization';
      const orgDocRef = doc(this.firestore, 'organizations', orgId);
      const orgSnap = await getDocs(
        query(
          collection(this.firestore, 'organizations'),
          where('__name__', '==', orgId),
        ),
      );
      if (!orgSnap.empty) {
        const orgData = orgSnap.docs[0].data();
        orgName = orgData?.['name'] || 'Unknown Organization';
      }

      const fileExtension = this.selectedFile.name
        .split('.')
        .pop()
        ?.toLowerCase();
      const fileName = `schedule_${orgId}_${Date.now()}.${fileExtension}`;
      const filePath = `schedules/${fileName}`;
      const storageRef = ref(this.storage, filePath);

      await uploadBytes(storageRef, this.selectedFile);
      const fileUrl = await getDownloadURL(storageRef);

      await addDoc(collection(this.firestore, 'schedules'), {
        title: title || 'Weekly Schedule',
        weekStartDate,
        fileName,
        fileUrl,
        fileType: fileExtension,
        uploadedAt: new Date().toISOString(),
        orgId,
        orgName,
        uploadedBy: this.auth.getUserName() || 'Unknown User',
      });

      this.showToast(`Schedule uploaded for ${orgName}!`, 'success');
      this.uploadScheduleForm.reset();
      this.selectedFile = null;

      const modal = bootstrap.Modal.getInstance('#uploadScheduleModal');
      modal?.hide();
    } catch (err) {
      console.error('Error uploading schedule:', err);
      this.showToast('Failed to upload schedule.', 'error');
    } finally {
      this.isUploadingSchedule = false;
    }
  }

  async downloadEmployeesJson(): Promise<void> {
    try {
      const orgId = this.auth.getUsersOrgId();
      if (!orgId) {
        console.error('No orgId found for current user.');
        return;
      }

      const employeesRef = collection(this.firestore, 'Employees');
      const q = query(
        employeesRef,
        where('role', 'in', ['employee', 'manager']),
        where('orgId', '==', orgId),
      );

      const employees$ = collectionData(q, { idField: 'id' });
      const employees = await firstValueFrom(employees$);

      const filteredEmployees = employees.map((emp: any) => ({
        name: emp.Name || null,
        availability: emp.availability
          ? {
              Sunday: emp.availability.Sunday || 'off',
              Monday: emp.availability.Monday || 'off',
              Tuesday: emp.availability.Tuesday || 'off',
              Wednesday: emp.availability.Wednesday || 'off',
              Thursday: emp.availability.Thursday || 'off',
              Friday: emp.availability.Friday || 'off',
              Saturday: emp.availability.Saturday || 'off',
            }
          : null,
        requestedOff: emp.requestedOff || [],
        fullTime: emp.fullTime ?? false,
        keyHolder: emp.keyHolder ?? false,
        role: emp.role ?? null,
        orgId: emp.orgId ?? null,
      }));

      const blob = new Blob([JSON.stringify(filteredEmployees, null, 2)], {
        type: 'application/json',
      });

      saveAs(blob, 'employees.json');
    } catch (err) {
      console.error('❌ Failed to fetch or download employees:', err);
    }
  }

  async toggleEmployeeList(): Promise<void> {
    this.showEmployees = !this.showEmployees;
    if (!this.showEmployees || this.employees.length) return;

    const ref = collection(this.firestore, 'Employees');
    const snapshot = await firstValueFrom(
      collectionData(ref, { idField: 'id' }),
    );
    this.employees = snapshot.map((emp: any) => {
      const availability = emp.availability || {};
      const allOff = Object.values(availability).every(
        (v: any) => String(v || '').toLowerCase() === 'off',
      );
      return { ...emp, incompleteAvailability: allOff };
    });
  }

  // ⚡ OPTIMIZED: Use cached count instead of filtering
  hasInactiveRequests(): boolean {
    return this.inactiveRequestsCount > 0;
  }

  // ⚡ OPTIMIZED: Use cached count instead of filtering
  hasDeniedRequests(): boolean {
    return this.deniedRequestsCount > 0;
  }

  // ⚡ OPTIMIZED: Return cached count
  getInactiveRequestsCount(): number {
    return this.inactiveRequestsCount;
  }

  // ⚡ OPTIMIZED: Return cached count
  getDeniedRequestsCount(): number {
    return this.deniedRequestsCount;
  }

  // ⚡ NEW: Calculate counts once after loading data
  private calculateRequestCounts(): void {
    this.inactiveRequestsCount = 0;
    this.deniedRequestsCount = 0;

    for (const req of this.requestsOff) {
      if (this.isInactiveRequest(req)) {
        this.inactiveRequestsCount++;
      }
      if (req.status === 'declined') {
        this.deniedRequestsCount++;
      }
    }
  }

  openEditOffcanvas(emp: any) {
    this.selectedEmployee = emp;

    const offcanvasElement = document.getElementById('editEmployeeOffcanvas');
    if (offcanvasElement) {
      const offcanvas =
        bootstrap.Offcanvas.getInstance(offcanvasElement) ||
        new bootstrap.Offcanvas(offcanvasElement);
      offcanvas.show();
    }

    this.editEmployeeForm.patchValue({
      Name: emp.Name,
      Pin: emp.Pin,
      fullTime: emp.fullTime,
      keyHolder: emp.keyHolder,
      notes: emp.notes || '',
      ...emp.availability,
    });
  }

  async onEditEmployeeSubmit() {
    if (!this.selectedEmployee) return;
    const v = this.editEmployeeForm.value;

    const empRef = doc(this.firestore, 'Employees', this.selectedEmployee.id);
    const updatedData = {
      Name: v.Name,
      Pin: v.Pin,
      fullTime: v.fullTime,
      keyHolder: v.keyHolder,
      notes: v.notes,
      availability: Object.fromEntries(
        this.dayKeys.map((d) => [d, v[d] || 'off']),
      ),
    };

    try {
      await updateDoc(empRef, updatedData);
      Object.assign(this.selectedEmployee, updatedData);
      this.showToast(`Updated ${v.Name} successfully!`, 'success');
      const offcanvas = bootstrap.Offcanvas.getInstance(
        '#editEmployeeOffcanvas',
      );
      offcanvas?.hide();
    } catch (err) {
      console.error('Error updating employee:', err);
      this.showToast('Failed to update employee.', 'error');
    }
  }

  async countFilteredRequests(): Promise<void> {
    try {
      const orgId = this.auth.getUsersOrgId();
      if (!orgId) {
        this.requestsCount = 0;
        return;
      }

      const ref = collection(this.firestore, 'requestsOff');
      let q;

      if (this.filterStatus && this.filterStatus !== 'all') {
        q = query(
          ref,
          where('orgId', '==', orgId),
          where('status', '==', this.filterStatus),
        );
      } else {
        q = query(ref, where('orgId', '==', orgId));
      }

      const snapshot = await getDocs(q);
      this.requestsCount = snapshot.size;
    } catch (error) {
      console.error('Error counting filtered requests:', error);
      this.requestsCount = 0;
    }
  }

  async removeAllInactiveRequests(): Promise<void> {
    const inactiveRequests = this.requestsOff.filter((req) =>
      this.isInactiveRequest(req),
    );

    if (inactiveRequests.length === 0) {
      this.showToast('No inactive requests to remove.', 'error');
      return;
    }

    const confirmDelete = confirm(
      `Are you sure you want to remove ${inactiveRequests.length} inactive request(s)? This action cannot be undone.`,
    );

    if (!confirmDelete) return;

    this.isDeletingInactive = true;

    try {
      const orgId = this.auth.getUsersOrgId();
      if (!orgId) {
        this.showToast('Organization not found.', 'error');
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      for (const req of inactiveRequests) {
        try {
          const requestRef = doc(this.firestore, 'requestsOff', req.id!);
          await (await import('@angular/fire/firestore')).deleteDoc(requestRef);

          const employeesRef = collection(this.firestore, 'Employees');
          const empQuery = query(
            employeesRef,
            where('Name', '==', req.employee),
            where('orgId', '==', orgId),
          );
          const empSnapshot = await getDocs(empQuery);

          if (!empSnapshot.empty) {
            const empDoc = empSnapshot.docs[0];
            const empRef = doc(this.firestore, 'Employees', empDoc.id);
            const empData = empDoc.data();

            const requestedOff = empData['requestedOff'] || [];
            const filtered = requestedOff.filter(
              (r: any) =>
                new Date(r.date).toISOString() !==
                new Date(req.date).toISOString(),
            );

            await updateDoc(empRef, { requestedOff: filtered });
          }

          successCount++;
        } catch (err) {
          console.error(`Error removing request for ${req.employee}:`, err);
          errorCount++;
        }
      }

      await this.loadRequestsOff('reset');
      await this.countPendingRequestsOff();
      this.calculateRequestCounts(); // ⚡ Recalculate counts

      if (errorCount > 0) {
        this.showToast(
          `Removed ${successCount} inactive request(s). ${errorCount} failed.`,
          'error',
        );
      } else {
        this.showToast(
          `Successfully removed ${successCount} inactive request(s)!`,
          'success',
        );
      }
    } catch (err) {
      console.error('Error removing inactive requests:', err);
      this.showToast('Failed to remove inactive requests.', 'error');
    } finally {
      this.isDeletingInactive = false;
    }
  }

  async removeAllDeniedRequests(): Promise<void> {
    const deniedRequests = this.requestsOff.filter(
      (req) => req.status === 'declined',
    );

    if (deniedRequests.length === 0) {
      this.showToast('No declined requests to remove.', 'error');
      return;
    }

    const confirmDelete = confirm(
      `Are you sure you want to remove ${deniedRequests.length} declined request(s)? This action cannot be undone.`,
    );

    if (!confirmDelete) return;

    this.isDeletingDeclined = true;

    try {
      const orgId = this.auth.getUsersOrgId();
      if (!orgId) {
        this.showToast('Organization not found.', 'error');
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      for (const req of deniedRequests) {
        try {
          const requestRef = doc(this.firestore, 'requestsOff', req.id!);
          await (await import('@angular/fire/firestore')).deleteDoc(requestRef);

          const employeesRef = collection(this.firestore, 'Employees');
          const empQuery = query(
            employeesRef,
            where('Name', '==', req.employee),
            where('orgId', '==', orgId),
          );
          const empSnapshot = await getDocs(empQuery);

          if (!empSnapshot.empty) {
            const empDoc = empSnapshot.docs[0];
            const empRef = doc(this.firestore, 'Employees', empDoc.id);
            const empData = empDoc.data();

            const requestedOff = empData['requestedOff'] || [];
            const filtered = requestedOff.filter(
              (r: any) =>
                new Date(r.date).toISOString() !==
                new Date(req.date).toISOString(),
            );

            await updateDoc(empRef, { requestedOff: filtered });
          }

          successCount++;
        } catch (err) {
          console.error(
            `Error removing declined request for ${req.employee}:`,
            err,
          );
          errorCount++;
        }
      }

      await this.loadRequestsOff('reset');
      await this.countPendingRequestsOff();
      await this.countFilteredRequests();
      this.calculateRequestCounts(); // ⚡ Recalculate counts

      if (errorCount > 0) {
        this.showToast(
          `Removed ${successCount} declined request(s). ${errorCount} failed.`,
          'error',
        );
      } else {
        this.showToast(
          `Successfully removed ${successCount} declined request(s)!`,
          'success',
        );
      }
    } catch (err) {
      console.error('Error removing declined requests:', err);
      this.showToast('Failed to remove declined requests.', 'error');
    } finally {
      this.isDeletingDeclined = false;
    }
  }

  async onFilterChange(): Promise<void> {
    this.requestsOff = [];
    this.firstVisible = null;
    this.lastVisible = null;
    this.currentPage = 1;

    await this.countFilteredRequests();
    await this.loadRequestsOff('reset');
  }

  async onSortChange(newSort: 'createdAt' | 'date') {
    this.sortBy = newSort;
    this.requestsOff.sort((a, b) => {
      const aVal = new Date(a[this.sortBy]).getTime();
      const bVal = new Date(b[this.sortBy]).getTime();
      return bVal - aVal;
    });
  }

  async loadRequestsOff(direction: 'next' | 'prev' | 'reset' = 'reset') {
    const orgId = this.auth.getUsersOrgId();
    if (!orgId) {
      this.showToast('Organization not found. Please log in again.', 'error');
      return;
    }

    await this.countFilteredRequests();
    this.totalPages = Math.ceil(this.requestsCount / this.pageSize) || 1;

    const ref = collection(this.firestore, 'requestsOff');
    const isAll = !this.filterStatus || this.filterStatus === 'all';

    if (direction === 'reset') {
      this.firstVisible = null;
      this.lastVisible = null;
      this.currentPage = 1;
      this.requestsOff = [];
      // ⚡ Reset counts on reset
      this.inactiveRequestsCount = 0;
      this.deniedRequestsCount = 0;
    } else if (direction === 'next') {
      this.currentPage++;
    } else if (direction === 'prev') {
      this.currentPage = Math.max(1, this.currentPage - 1);
    }

    try {
      let snapshot;

      if (isAll) {
        let baseQuery;
        if (direction === 'next' && this.lastVisible) {
          baseQuery = query(
            ref,
            where('orgId', '==', orgId),
            orderBy('createdAt', 'desc'),
            startAfter(this.lastVisible),
            limit(this.pageSize),
          );
        } else if (direction === 'prev' && this.firstVisible) {
          baseQuery = query(
            ref,
            where('orgId', '==', orgId),
            orderBy('createdAt', 'desc'),
            endBefore(this.firstVisible),
            limit(this.pageSize),
          );
        } else {
          baseQuery = query(
            ref,
            where('orgId', '==', orgId),
            orderBy('createdAt', 'desc'),
            limit(this.pageSize),
          );
        }

        snapshot = await getDocs(baseQuery);

        const docs = snapshot.docs;
        if (!docs.length) {
          this.requestsOff = [];
          this.isNextDisabled = true;
          this.isPrevDisabled = true;
          // ⚡ Reset counts when no data
          this.inactiveRequestsCount = 0;
          this.deniedRequestsCount = 0;
          return;
        }

        this.firstVisible = docs[0];
        this.lastVisible = docs[docs.length - 1];

        this.requestsOff = docs.map((d) => ({
          ...(d.data() as RequestOff),
          id: d.id,
        }));
        this.isPrevDisabled = this.currentPage <= 1;
        this.isNextDisabled =
          this.currentPage >= this.totalPages || docs.length < this.pageSize;
      } else {
        const filteredQuery = query(
          ref,
          where('orgId', '==', orgId),
          where('status', '==', this.filterStatus),
        );

        snapshot = await getDocs(filteredQuery);

        const all = snapshot.docs.map((d) => ({
          ...(d.data() as RequestOff),
          id: d.id,
        }));

        all.sort((a, b) => {
          const ad = new Date(a.createdAt).getTime();
          const bd = new Date(b.createdAt).getTime();
          return bd - ad;
        });

        const start = (this.currentPage - 1) * this.pageSize;
        const end = start + this.pageSize;
        this.requestsOff = all.slice(start, end);

        this.isPrevDisabled = this.currentPage === 1;
        this.isNextDisabled = end >= all.length;
      }

      // ⚡ CALCULATE COUNTS ONCE after loading data
      this.calculateRequestCounts();
    } catch (err) {
      console.error('Error loading requests:', err);
      this.showToast('Failed to load requests from Firestore.', 'error');
    }
  }

  async updateRequestStatus(request: any, newStatus: 'approved' | 'declined') {
    try {
      const requestRef = doc(this.firestore, 'requestsOff', request.id);
      await updateDoc(requestRef, {
        status: newStatus,
        approvedBy: this.userName,
      });

      const formattedDate = new Date(request.date).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });

      const employeesRef = collection(this.firestore, 'Employees');
      const q = query(employeesRef, where('Name', '==', request.employee));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const employeeDoc = snapshot.docs[0];
        const employeeRef = doc(this.firestore, 'Employees', employeeDoc.id);
        const employeeData = employeeDoc.data();

        const requestedOff = employeeData['requestedOff'] || [];

        const existingIndex = requestedOff.findIndex(
          (r: any) => r.date === formattedDate,
        );

        if (existingIndex !== -1) {
          requestedOff[existingIndex] = {
            ...requestedOff[existingIndex],
            status: newStatus,
            approvedBy: this.userName,
          };
        } else {
          requestedOff.push({
            date: formattedDate,
            status: newStatus,
            approvedBy: this.userName,
          });
        }

        await updateDoc(employeeRef, { requestedOff });
      }

      request.status = newStatus;
      this.showToast(
        `Request for ${request.employee} marked as ${newStatus}!`,
        'success',
      );
      await this.countPendingRequestsOff();
      this.calculateRequestCounts(); // ⚡ Recalculate counts after status change
    } catch (err) {
      console.error('Error updating request:', err);
      this.showToast('Failed to update request.', 'error');
    }
  }

  async countPendingRequestsOff(): Promise<void> {
    try {
      const orgId = this.auth.getUsersOrgId();
      if (!orgId) {
        this.pendingCount = 0;
        return;
      }

      const ref = collection(this.firestore, 'requestsOff');
      const q = query(
        ref,
        where('orgId', '==', orgId),
        where('status', '==', 'pending'),
      );
      const snapshot = await getDocs(q);
      this.pendingCount = snapshot.size;
    } catch (error) {
      console.error('Error counting pending requests:', error);
      this.pendingCount = 0;
    }
    return;
  }

  async countAllRequestsOff(): Promise<void> {
    try {
      const orgId = this.auth.getUsersOrgId();
      if (!orgId) {
        this.requestsCount = 0;
        return;
      }

      const ref = collection(this.firestore, 'requestsOff');
      const q = query(ref, where('orgId', '==', orgId));
      const snapshot = await getDocs(q);
      this.requestsCount = snapshot.size;
    } catch (error) {
      console.error('Error counting all requests:', error);
      this.requestsCount = 0;
    }
    return;
  }

  showToast(message: string, type: 'success' | 'error') {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;
    setTimeout(() => (this.toastVisible = false), 3000);
  }

  goToManageEmployees() {
    this.router.navigate(['/manage-employees']);
  }

  isInactiveRequest(req: RequestOff): boolean {
    if (!req.date) return false;

    const requestedDate = new Date(req.date);
    const now = new Date();

    const diffDays =
      (now.getTime() - requestedDate.getTime()) / (1000 * 60 * 60 * 24);

    return diffDays > 14;
  }

  async removeInactiveRequest(req: RequestOff): Promise<void> {
    try {
      const orgId = this.auth.getUsersOrgId();
      if (!orgId) {
        this.showToast('Organization not found.', 'error');
        return;
      }

      const requestRef = doc(this.firestore, 'requestsOff', req.id!);
      await updateDoc(requestRef, { status: 'deleted' }).catch(() => {});
      await (await import('@angular/fire/firestore')).deleteDoc(requestRef);

      const employeesRef = collection(this.firestore, 'Employees');
      const empQuery = query(
        employeesRef,
        where('Name', '==', req.employee),
        where('orgId', '==', orgId),
      );
      const empSnapshot = await getDocs(empQuery);

      if (!empSnapshot.empty) {
        const empDoc = empSnapshot.docs[0];
        const empRef = doc(this.firestore, 'Employees', empDoc.id);
        const empData = empDoc.data();

        const requestedOff = empData['requestedOff'] || [];
        const filtered = requestedOff.filter(
          (r: any) =>
            new Date(r.date).toISOString() !== new Date(req.date).toISOString(),
        );

        await updateDoc(empRef, { requestedOff: filtered });
      }

      this.requestsOff = this.requestsOff.filter((r) => r.id !== req.id);
      this.calculateRequestCounts(); // ⚡ Recalculate counts after removal
      this.showToast(`Removed inactive request for ${req.employee}`, 'success');
    } catch (err) {
      console.error('Error removing inactive request:', err);
      this.showToast('Failed to remove inactive request.', 'error');
    }
  }

  goToSwapShifts() {
    this.router.navigate(['/shift-swap']);
  }

  goToCreateSchedule() {
    this.router.navigate(['/schedule']);
  }

  goToManageTimesheets() {
    this.router.navigate(['/manager/timesheets']);
  }

  goToPublicSchedule() {
    this.router.navigate(['/public-schedule']);
  }

  async onPageSizeChange(): Promise<void> {
    this.requestsOff = [];
    this.firstVisible = null;
    this.lastVisible = null;
    this.currentPage = 1;

    await this.countFilteredRequests();
    await this.loadRequestsOff('reset');
  }
}
